import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Modal } from './modal';
import { ModalActions } from './modal-actions';
import type { ButtonProps } from '@/components/ui/button';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  /**
   * O que vai acontecer, em termos concretos. "Tem certeza?" não é descrição — quem lê
   * precisa saber o que perde, e se dá para desfazer.
   */
  description: ReactNode;
  confirmLabel?: string;
  /**
   * O rótulo do botão que **desiste**.
   *
   * Existe por causa dos diálogos que confirmam um cancelamento: com o padrão "Cancelar", a
   * caixa fica com dois botões começando pela mesma palavra — "Cancelar" e "Cancelar
   * convite" — e quem está com pressa clica no errado. Nesses casos, "Voltar".
   */
  cancelLabel?: string;
  confirmIcon?: ReactNode;
  confirmVariant?: ButtonProps['variant'];
  isLoading?: boolean;
}

/**
 * Confirmação de ação destrutiva ou irreversível.
 *
 * O padrão é destrutivo — rótulo "Excluir", botão vermelho — porque é para isso que
 * confirmação existe neste sistema. Ação reversível não deve abrir modal: interrompe o
 * trabalho para perguntar sobre algo que a pessoa pode simplesmente desfazer.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Excluir',
  cancelLabel,
  confirmIcon = <Trash2 aria-hidden />,
  confirmVariant = 'destructive',
  isLoading = false,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <ModalActions
          onCancel={() => onOpenChange(false)}
          onConfirm={onConfirm}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          confirmIcon={confirmIcon}
          confirmVariant={confirmVariant}
          isLoading={isLoading}
        />
      }
    >
      <p className="text-sm text-muted-foreground">{description}</p>
    </Modal>
  );
}
